import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { IconClose } from './icons';
import styles from './toast.module.css';

export type ToastTone = 'info' | 'success' | 'error' | 'warn';

export interface Toast {
  id: string;
  tone: ToastTone;
  title: string;
  description?: string;
  /** Milliseconds before auto-dismiss. Errors stay until dismissed. */
  durationMs?: number;
}

export type ToastInput = Omit<Toast, 'id'>;

interface ToastContextValue {
  toasts: Toast[];
  push: (toast: ToastInput) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let toastSequence = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      toastSequence += 1;
      const id = `toast-${toastSequence}`;
      const toast: Toast = { id, ...input };
      setToasts((current) => [...current, toast]);

      const duration = input.durationMs ?? (input.tone === 'error' ? 0 : 5_000);
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        );
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const value = useMemo(() => ({ toasts, push, dismiss }), [toasts, push, dismiss]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used inside a <ToastProvider>.');
  return context;
}

/**
 * Renders the stack. Mounted once per shell so a toast raised from a drawer
 * still appears above the page rather than inside the drawer.
 */
export function ToastHost() {
  const { toasts, dismiss } = useToast();

  return (
    <div className={styles.host} role="region" aria-label="Notifications">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={[styles.toast, styles[toast.tone]].filter(Boolean).join(' ')}
          role={toast.tone === 'error' ? 'alert' : 'status'}
          aria-live={toast.tone === 'error' ? 'assertive' : 'polite'}
        >
          <span className={styles.rail} aria-hidden="true" />
          <span className={styles.text}>
            <span className={styles.title}>{toast.title}</span>
            {toast.description ? (
              <span className={styles.description}>{toast.description}</span>
            ) : null}
          </span>
          <button
            type="button"
            className={styles.dismiss}
            onClick={() => dismiss(toast.id)}
            aria-label={`Dismiss: ${toast.title}`}
          >
            <IconClose size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
